import { useCallback } from 'react';
import { DialogSection, DialogField, DialogOption, ConditionOption } from '@/lib/stores/dialogStore';
import { DragEndEvent } from '@dnd-kit/core';
import { arrayMove } from '@dnd-kit/sortable';
import { generateSectionIdentifier } from '../utils/dialogUtils';

export interface UseDialogSectionHandlersProps {
  sections: DialogSection[];
  setSections: React.Dispatch<React.SetStateAction<DialogSection[]>>;
  toast: (options: { title: string; description: string; variant?: 'default' | 'destructive' }) => void;
}

export interface DialogSectionHandlers {
  // Section handlers
  addSection: () => void;
  removeSection: (index: number) => void;
  updateSection: (index: number, updates: Partial<DialogSection>) => void;
  handleSectionDragEnd: (event: DragEndEvent) => void;
  
  // Field handlers
  addField: (sectionIndex: number) => void;
  updateField: (sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => void;
  removeField: (sectionIndex: number, fieldId: string) => void;
  
  // Option handlers
  addOption: (sectionIndex: number, fieldId: string) => void;
  updateOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<DialogOption>) => void;
  removeOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
  
  // Condition option handlers
  addConditionOption: (sectionIndex: number, fieldId: string) => void;
  updateConditionOption: (sectionIndex: number, fieldId: string, optionIndex: number, updates: Partial<ConditionOption>) => void;
  removeConditionOption: (sectionIndex: number, fieldId: string, optionIndex: number) => void;
}

/**
 * Custom hook for section, field, and option CRUD operations
 */
export function useDialogSectionHandlers({
  sections,
  setSections,
  toast,
}: UseDialogSectionHandlersProps): DialogSectionHandlers {
  
  // ============================================================================
  // SECTION HANDLERS
  // ============================================================================
  
  const addSection = useCallback(() => {
    setSections(prevSections => {
      const sectionId = `section_${Date.now()}`;
      const fieldId = `field_${Date.now()}`;
      const newSection: DialogSection = {
        id: sectionId,
        name: `Section ${prevSections.length + 1}`,
        section_identifier: generateSectionIdentifier(),
        order_index: prevSections.length,
        fields: [{
          id: fieldId,
          type: 'dialog',
          text: '',
          translatable: true,
          order_index: 0,
        }],
        fieldOrder: [fieldId],
      };
      return [...prevSections, newSection];
    });
  }, [setSections]);

  const removeSection = useCallback((index: number) => {
    setSections(prevSections => {
      const sectionToRemove = prevSections[index];
      if (!sectionToRemove) return prevSections;
      
      const newSections = prevSections.filter((_, i) => i !== index);
      
      // Clean up all references to the removed section
      const removedIdentifier = sectionToRemove.section_identifier;
      
      newSections.forEach(section => {
        section.fields?.forEach(field => {
          // Clean up field-level next_section references
          if (field.next_section === removedIdentifier) {
            field.next_section = null;
          }
          
          // Clean up condition field branches
          if (field.type === 'condition') {
            if (field.next_section_true === removedIdentifier) {
              field.next_section_true = null;
            }
            if (field.next_section_false === removedIdentifier) {
              field.next_section_false = null;
            }
          }
          
          // Clean up option references
          if (field.type === 'question' && field.options) {
            field.options.forEach((option, optIdx) => {
              if (option.next_section === removedIdentifier) {
                // Preserve other option properties when clearing reference
                field.options![optIdx] = {
                  ...option,
                  next_section: null,
                };
              }
            });
          }
        });
      });
      
      // Update order indices
      const reorderedSections = newSections.map((section, i) => ({
        ...section,
        order_index: i,
      }));
      
      // Show toast outside of setState
      setTimeout(() => {
        toast({
          title: 'Section Removed',
          description: `Section "${sectionToRemove.name}" and all references to it have been removed`,
        });
      }, 0);
      
      return reorderedSections;
    });
  }, [setSections, toast]);

  const updateSection = useCallback((index: number, updates: Partial<DialogSection>) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      newSections[index] = { ...newSections[index], ...updates };
      return newSections;
    });
  }, [setSections]);

  const handleSectionDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    setSections(prevSections => {
      // Find sections by their stable IDs
      const activeIndex = prevSections.findIndex(s => s.id === active.id);
      const overIndex = prevSections.findIndex(s => s.id === over.id);

      if (activeIndex !== -1 && overIndex !== -1) {
        const reorderedSections = arrayMove(prevSections, activeIndex, overIndex);
        // Update order indices
        return reorderedSections.map((section, i) => ({
          ...section,
          order_index: i,
        }));
      }
      return prevSections;
    });
  }, [setSections]);

  // ============================================================================
  // FIELD HANDLERS
  // ============================================================================

  const addField = useCallback((sectionIndex: number) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = { ...newSections[sectionIndex] };
      const fieldId = `field_${Date.now()}`;
      const newField: DialogField = {
        id: fieldId,
        type: 'dialog',
        text: '',
        translatable: true,
        order_index: section.fields?.length || 0,
      };
      section.fields = [...(section.fields || []), newField];
      section.fieldOrder = [...(section.fieldOrder || []), fieldId];
      newSections[sectionIndex] = section;
      return newSections;
    });
  }, [setSections]);

  const updateField = useCallback((sectionIndex: number, fieldId: string, updates: Partial<DialogField>) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = { ...newSections[sectionIndex] };
      if (section.fields) {
        section.fields = section.fields.map(field => {
          if (field.id !== fieldId) return field;
          
          let merged = { ...field, ...updates };
          
          // When the type changes, clean up properties that belong to the old type
          // to prevent stale data from reappearing if the user switches back later
          if (updates.type && updates.type !== field.type) {
            // Clear question-specific properties when leaving 'question' type
            if (field.type === 'question' && updates.type !== 'question') {
              merged.options = undefined;
            }
            // Clear condition-specific properties when leaving 'condition' type
            if (field.type === 'condition' && updates.type !== 'condition') {
              merged.condition = undefined;
              merged.conditionOptions = undefined;
            }
            // Clear setter-specific properties when leaving 'setter' type
            if (field.type === 'setter' && updates.type !== 'setter') {
              merged.setter = undefined;
            }
          }
          
          return merged;
        });
      }
      newSections[sectionIndex] = section;
      return newSections;
    });
  }, [setSections]);

  const removeField = useCallback((sectionIndex: number, fieldId: string) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = { ...newSections[sectionIndex] };
      section.fields = section.fields?.filter(field => field.id !== fieldId) || [];
      section.fieldOrder = section.fieldOrder?.filter(id => id !== fieldId) || [];
      newSections[sectionIndex] = section;
      return newSections;
    });
  }, [setSections]);

  // ============================================================================
  // OPTION HANDLERS
  // ============================================================================

  const addOption = useCallback((sectionIndex: number, fieldId: string) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = { ...newSections[sectionIndex] };
      const fieldIndex = section.fields?.findIndex(f => f.id === fieldId) ?? -1;
      if (fieldIndex >= 0 && section.fields) {
        const field = { ...section.fields[fieldIndex] };
        const newOption: DialogOption = {
          option_text: '',
          translatable: true,
          next_section: null,
          order_index: field.options?.length || 0,
        };
        field.options = [...(field.options || []), newOption];
        section.fields = [...section.fields];
        section.fields[fieldIndex] = field;
      }
      newSections[sectionIndex] = section;
      return newSections;
    });
  }, [setSections]);

  const updateOption = useCallback((
    sectionIndex: number,
    fieldId: string,
    optionIndex: number,
    updates: Partial<DialogOption>
  ) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = { ...newSections[sectionIndex] };
      const fieldIndex = section.fields?.findIndex(f => f.id === fieldId) ?? -1;
      if (fieldIndex >= 0 && section.fields) {
        const field = { ...section.fields[fieldIndex] };
        if (field.options) {
          field.options = [...field.options];
          field.options[optionIndex] = { ...field.options[optionIndex], ...updates };
        }
        section.fields = [...section.fields];
        section.fields[fieldIndex] = field;
      }
      newSections[sectionIndex] = section;
      return newSections;
    });
  }, [setSections]);

  const removeOption = useCallback((sectionIndex: number, fieldId: string, optionIndex: number) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = { ...newSections[sectionIndex] };
      const fieldIndex = section.fields?.findIndex(f => f.id === fieldId) ?? -1;
      if (fieldIndex >= 0 && section.fields) {
        const field = { ...section.fields[fieldIndex] };
        field.options = field.options?.filter((_, i) => i !== optionIndex) || [];
        // Update order indices
        field.options = field.options.map((opt, i) => ({
          ...opt,
          order_index: i,
        }));
        section.fields = [...section.fields];
        section.fields[fieldIndex] = field;
      }
      newSections[sectionIndex] = section;
      return newSections;
    });
  }, [setSections]);

  // ============================================================================
  // CONDITION OPTION HANDLERS
  // ============================================================================

  const addConditionOption = useCallback((sectionIndex: number, fieldId: string) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = { ...newSections[sectionIndex] };
      const fieldIndex = section.fields?.findIndex(f => f.id === fieldId) ?? -1;
      if (fieldIndex >= 0 && section.fields) {
        const field = { ...section.fields[fieldIndex] };
        const newOption: ConditionOption = {
          id: `${fieldId}-opt-${Date.now()}`,
          condition_value: 0,
          operator: '==',
          order_index: field.conditionOptions?.length || 0,
        };
        field.conditionOptions = [...(field.conditionOptions || []), newOption];
        section.fields = [...section.fields];
        section.fields[fieldIndex] = field;
      }
      newSections[sectionIndex] = section;
      return newSections;
    });
  }, [setSections]);

  const updateConditionOption = useCallback((
    sectionIndex: number,
    fieldId: string,
    optionIndex: number,
    updates: Partial<ConditionOption>
  ) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = { ...newSections[sectionIndex] };
      const fieldIndex = section.fields?.findIndex(f => f.id === fieldId) ?? -1;
      if (fieldIndex >= 0 && section.fields) {
        const field = { ...section.fields[fieldIndex] };
        if (field.conditionOptions) {
          field.conditionOptions = [...field.conditionOptions];
          field.conditionOptions[optionIndex] = { ...field.conditionOptions[optionIndex], ...updates };
        }
        section.fields = [...section.fields];
        section.fields[fieldIndex] = field;
      }
      newSections[sectionIndex] = section;
      return newSections;
    });
  }, [setSections]);

  const removeConditionOption = useCallback((sectionIndex: number, fieldId: string, optionIndex: number) => {
    setSections(prevSections => {
      const newSections = [...prevSections];
      const section = { ...newSections[sectionIndex] };
      const fieldIndex = section.fields?.findIndex(f => f.id === fieldId) ?? -1;
      if (fieldIndex >= 0 && section.fields) {
        const field = { ...section.fields[fieldIndex] };
        field.conditionOptions = field.conditionOptions?.filter((_, i) => i !== optionIndex) || [];
        // Update order indices
        field.conditionOptions = field.conditionOptions.map((opt, i) => ({
          ...opt,
          order_index: i,
        }));
        section.fields = [...section.fields];
        section.fields[fieldIndex] = field;
      }
      newSections[sectionIndex] = section;
      return newSections;
    });
  }, [setSections]);

  return {
    // Section handlers
    addSection,
    removeSection,
    updateSection,
    handleSectionDragEnd,
    
    // Field handlers
    addField,
    updateField,
    removeField,
    
    // Option handlers
    addOption,
    updateOption,
    removeOption,
    
    // Condition option handlers
    addConditionOption,
    updateConditionOption,
    removeConditionOption,
  };
}

